import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import SystemsHeader from '../../components/SystemsHeader';
import SystemsFooter from '../../components/SystemsFooter';
import SeoHead from '../../components/SeoHead';
import { Check, ArrowLeft, ArrowRight, User, Users, Building2, Send, Sparkles } from 'lucide-react';
import { useSystemsCatalog } from '../../context/SystemsCatalogContext';
import { useCurrency } from '../../context/CurrencyContext';
import { recommendPlan } from '../../data/recommendPlan';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;

const SETUPS = {
  individual: {
    key: 'individual',
    title: 'Individual Support',
    eyebrow: 'For individuals',
    summary: 'For personal devices and home technology.',
    bullets: [
      'Device monitoring',
      'Security baseline protection',
      'Remote technical support',
      'System optimization',
    ],
    icon: User,
    cta: 'Start Individual Setup',
    popular: false,
    formTitle: 'Let\u2019s get you started.',
    businessField: false,
  },
  business: {
    key: 'business',
    title: 'Business IT Services',
    eyebrow: 'For small & mid-size teams',
    summary: 'For startups, small businesses, and growing teams.',
    bullets: [
      'Managed IT services',
      'Device monitoring and patch management',
      'Help desk support',
      'Infrastructure and cloud support',
    ],
    icon: Users,
    cta: 'Start Business Setup',
    popular: true,
    formTitle: 'Let\u2019s get you started.',
    businessField: true,
  },
  enterprise: {
    key: 'enterprise',
    title: 'Enterprise Solutions',
    eyebrow: 'For complex environments',
    summary: 'For organizations with complex infrastructure environments.',
    bullets: [
      'Infrastructure deployment',
      'Cybersecurity monitoring',
      'Backup and disaster recovery',
      'IT consulting and advisory',
    ],
    icon: Building2,
    cta: 'Contact Enterprise Team',
    popular: false,
    formTitle: 'Let\u2019s architect your setup.',
    businessField: true,
  },
};

const DEVICE_RANGES = ['1 - 5', '6 - 20', '21 - 50', '51 - 200', '200+'];
const SERVICE_INTERESTS = [
  'Managed IT Services',
  'Infrastructure Services',
  'Cybersecurity Services',
  'IT Consulting & Advisory',
];

export default function GetStarted() {
  const [params] = useSearchParams();
  const setupParam = params.get('setup');
  const setup = setupParam && SETUPS[setupParam] ? SETUPS[setupParam] : null;

  return (
    <div className="min-h-screen bg-white">
      <SeoHead path="/systems/get-started" title="Get Started — ZHELTA Systems" description="Start with ZHELTA Systems. Professional IT management, infrastructure support, and cybersecurity services for individuals and businesses." />
      <SystemsHeader />
      <main>
        {setup ? <SetupForm setup={setup} /> : <SetupPicker />}
      </main>
      <SystemsFooter />
    </div>
  );
}

function SetupPicker() {
  return (
    <>
      <section className="py-20 md:py-28 bg-[radial-gradient(ellipse_90%_70%_at_50%_20%,#ffffff_0%,#FDFCFB_45%,#f5f1ea_100%)]">
        <div className="zh-container max-w-4xl text-center">
          <span className="inline-block text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#c9a227]">Get Started</span>
          <h1 className="mt-4 text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">
            Start with ZHELTA<br /><span className="text-[#f5a623]">Systems.</span>
          </h1>
          <p className="mt-6 text-[17px] text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Professional IT management, infrastructure support, and cybersecurity services for individuals and businesses.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-white">
        <div className="zh-container max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Choose Your Setup</h2>
            <p className="mt-3 text-gray-600">Select the option that best fits your needs.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {Object.values(SETUPS).map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.key}
                  data-testid={`get-started-card-${s.key}`}
                  className={`relative rounded-2xl bg-white p-7 flex flex-col ${s.popular ? 'border-2 border-[#f5a623] shadow-[0_16px_48px_-16px_rgba(245,166,35,0.5)] md:scale-[1.03]' : 'border border-gray-200'}`}
                >
                  {s.popular && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#f5a623] text-black text-[10px] font-extrabold tracking-[0.18em]">MOST POPULAR</span>
                  )}
                  <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center mb-4">
                    <Icon className="w-5 h-5 text-[#c9a227]" strokeWidth={2} />
                  </div>
                  <div className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-gray-400">{s.eyebrow}</div>
                  <h3 className="mt-1 text-xl font-bold text-gray-900">{s.title}</h3>
                  <p className="mt-2 text-[14px] text-gray-600 leading-relaxed min-h-[3rem]">{s.summary}</p>
                  <div className="mt-5 text-[11px] font-bold text-gray-500 uppercase tracking-wider">Includes</div>
                  <ul className="mt-2 space-y-2 flex-1">
                    {s.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-2 text-[14px] text-gray-700">
                        <Check className="w-4 h-4 text-[#f5a623] mt-0.5 flex-shrink-0" strokeWidth={3} /><span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/systems/get-started?setup=${s.key}`}
                    data-testid={`get-started-cta-${s.key}`}
                    className={`mt-7 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-[13px] font-semibold uppercase tracking-wider transition ${s.popular ? 'bg-[#f5a623] text-[#0e0e10] hover:brightness-105 shadow-lg' : 'bg-[#f5a623] text-black hover:brightness-110'}`}
                  >
                    {s.cta} <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}

function SetupForm({ setup }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', devices: '', interest: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [countdown, setCountdown] = useState(6);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { getService, getTier } = useSystemsCatalog();

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  // Count down from 6s → redirect to the recommended pricing page.
  useEffect(() => {
    if (!sent || !recommendation) return undefined;
    if (countdown <= 0) {
      navigate(recommendation.url);
      return undefined;
    }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [sent, countdown, recommendation, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const messageBlob = [
        form.message,
        `Setup type: ${setup.title}`,
        form.devices ? `Device / user range: ${form.devices}` : '',
        form.interest ? `Service interest: ${form.interest}` : '',
      ].filter(Boolean).join('\n');
      // Compute recommendation up-front so we can persist it on the lead.
      const rec = recommendPlan({ setup: setup.key, interest: form.interest, devices: form.devices });
      await axios.post(`${API}/systems/leads`, {
        kind: 'get-started',
        slug: null,
        tier_id: setup.key,
        name: form.name,
        email: form.email,
        company: form.company || null,
        phone: form.phone || null,
        message: messageBlob,
        recommended_slug: rec?.slug || null,
        recommended_tier: rec?.tierId || null,
      });
      setRecommendation(rec);
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Could not submit');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="py-16 md:py-20 bg-[#fafafa] min-h-[70vh]">
      <div className="zh-container max-w-3xl">
        <Link to="/systems/get-started" data-testid="get-started-back" className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-gray-600 hover:text-black transition mb-6">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Get Started
        </Link>
        <span className="inline-block text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#c9a227]">{setup.title}</span>
        <h1 className="mt-3 text-4xl sm:text-5xl font-extrabold tracking-tight text-gray-900 leading-[1.05]">{setup.formTitle}</h1>
        <p className="mt-5 text-[16px] text-gray-600 leading-relaxed">
          Tell us a bit about yourself so we can personalize your setup experience.
        </p>

        {sent ? (
          <RecommendationCard
            setup={setup}
            recommendation={recommendation}
            countdown={countdown}
            onSkip={() => navigate(recommendation?.url || '/systems/solutions')}
            onStay={() => { setCountdown(-1); }}
            getService={getService}
            getTier={getTier}
          />
        ) : (
          <form onSubmit={submit} className="mt-10 rounded-2xl border border-gray-200 bg-white p-6 md:p-8 space-y-5" data-testid="get-started-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field required label="Full Name" value={form.name} onChange={set('name')} testId="gs-name" />
              <Field required type="email" label="Email Address" value={form.email} onChange={set('email')} testId="gs-email" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field required type="tel" label="Phone Number" value={form.phone} onChange={set('phone')} testId="gs-phone" />
              {setup.businessField && (
                <Field required label={setup.key === 'enterprise' ? 'Organization Name' : 'Business Name'} value={form.company} onChange={set('company')} testId="gs-company" />
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Select required label={setup.businessField ? 'Number of devices or users' : 'Number of devices'} value={form.devices} onChange={set('devices')} options={DEVICE_RANGES} placeholder="Select range" testId="gs-devices" />
              <Select required label="Service Interest" value={form.interest} onChange={set('interest')} options={SERVICE_INTERESTS} placeholder="Select primary interest" testId="gs-interest" />
            </div>
            <div>
              <label className="block text-[12px] font-semibold uppercase tracking-wider text-gray-600 mb-1.5">Your Message *</label>
              <p className="text-[12px] text-gray-500 mb-1.5">Additional details</p>
              <textarea
                required
                value={form.message}
                onChange={set('message')}
                rows={4}
                data-testid="gs-message"
                className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#f5a623] focus:ring-2 focus:ring-amber-100 outline-none transition"
              />
            </div>
            {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}
            <div className="flex items-center justify-between pt-2">
              <Link to="/systems/get-started" className="text-[13px] font-semibold text-gray-600 hover:text-black transition">Back</Link>
              <button
                type="submit"
                disabled={submitting}
                data-testid="gs-submit"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#f5a623] text-black text-[13px] font-bold uppercase tracking-wider hover:brightness-110 transition disabled:opacity-60"
              >
                {submitting ? 'Sending…' : <>Start My Setup <Send className="w-4 h-4" /></>}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

function Field({ label, value, onChange, type = 'text', required = false, testId }) {
  return (
    <div>
      <label className="block text-[12px] font-semibold uppercase tracking-wider text-gray-600 mb-1.5">{label}{required && ' *'}</label>
      <input
        type={type} value={value} onChange={onChange} required={required} data-testid={testId}
        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#f5a623] focus:ring-2 focus:ring-amber-100 outline-none transition"
      />
    </div>
  );
}

function Select({ label, value, onChange, options, placeholder, required = false, testId }) {
  return (
    <div>
      <label className="block text-[12px] font-semibold uppercase tracking-wider text-gray-600 mb-1.5">{label}{required && ' *'}</label>
      <select
        value={value} onChange={onChange} required={required} data-testid={testId}
        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:border-[#f5a623] focus:ring-2 focus:ring-amber-100 outline-none transition"
      >
        <option value="">{placeholder}</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}

function RecommendationCard({ setup, recommendation, countdown, onSkip, onStay, getService, getTier }) {
  const svc = recommendation ? getService(recommendation.slug) : null;
  const tier = recommendation ? getTier(recommendation.slug, recommendation.tierId) : null;
  const isRedirecting = countdown >= 0;
  const { format } = useCurrency();

  return (
    <div className="mt-10 space-y-6" data-testid="get-started-success">
      {/* Confirmation */}
      <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-8 text-center">
        <Check className="w-12 h-12 text-emerald-600 mx-auto mb-3" strokeWidth={2.5} />
        <h3 className="text-2xl font-bold text-gray-900">Setup request received</h3>
        <p className="text-gray-600 mt-2 max-w-xl mx-auto">
          A ZHELTA Systems specialist will be in touch within 1 business day to walk you through your {setup.title.toLowerCase()}.
        </p>
      </div>

      {/* Recommendation */}
      {svc && tier && (
        <div
          className="rounded-2xl border-2 border-[#f5a623] bg-gradient-to-br from-[#FEFBF3] to-white p-6 md:p-8 shadow-[0_16px_48px_-16px_rgba(245,166,35,0.35)]"
          data-testid="get-started-recommendation"
        >
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-[#c9a227]" strokeWidth={2.2} />
            <span className="text-[11px] font-extrabold uppercase tracking-[0.22em] text-[#c9a227]">Recommended for you</span>
          </div>
          <h4 className="text-2xl md:text-3xl font-extrabold text-gray-900">{svc.name}</h4>
          <p className="mt-2 text-gray-600 text-[15px] leading-relaxed">{svc.tagline}</p>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-5 items-end">
            <div className="rounded-xl border border-amber-100 bg-white px-4 py-3">
              <div className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-gray-500">Suggested tier</div>
              <div className="mt-1 text-lg font-bold text-gray-900">{tier.name}</div>
              <p className="text-[13px] text-gray-600 mt-0.5">{tier.description}</p>
            </div>
            <div className="text-right">
              <div className="text-[11px] text-gray-500">Starting at</div>
              <div className="text-3xl font-extrabold text-gray-900">{format(Math.round(tier.price))}</div>
              <div className="text-[11px] text-gray-500">/{svc.unit}</div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <div className="text-[13px] text-gray-600" data-testid="get-started-countdown">
              {isRedirecting
                ? <>Taking you there in <strong className="text-[#c9a227] tabular-nums">{countdown}s</strong>…</>
                : 'Ready when you are.'}
            </div>
            <div className="flex flex-wrap gap-2">
              {isRedirecting && (
                <button
                  onClick={onStay}
                  data-testid="get-started-stay"
                  className="px-4 py-2 rounded-full text-[12px] font-semibold uppercase tracking-wider text-gray-700 hover:text-black transition"
                >
                  Stay here
                </button>
              )}
              <button
                onClick={onSkip}
                data-testid="get-started-view-plan"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#f5a623] text-black text-[13px] font-bold uppercase tracking-wider hover:brightness-110 transition"
              >
                View my plan <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="text-center">
        <Link to="/systems" className="text-[13px] font-semibold text-gray-600 hover:text-black transition">← Back to Systems</Link>
      </div>
    </div>
  );
}

